import { useEffect, useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ChevronDown, LogOut, Search, X } from 'lucide-react';
import { roleLabel } from '@klothiq/domain';
import BrandLogo from '@/components/common/BrandLogo';
import { currentNavigation, navigationGroups, type NavigationItem } from '@/config/navigation';

export function NavigationSearch({
  value,
  onChange,
  label = 'Find a page',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return (
    <label className="studio-search">
      <Search aria-hidden="true" />
      <input
        type="search"
        aria-label={label}
        placeholder="Find a page…"
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
      />
      {value && (
        <button
          type="button"
          aria-label={label === 'Find a page' ? 'Clear page search' : 'Clear mobile page search'}
          onClick={() => onChange('')}
        >
          <X aria-hidden="true" />
        </button>
      )}
    </label>
  );
}

function Destination({
  item,
  compact,
  onNavigate,
}: {
  item: NavigationItem;
  compact: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      aria-label={item.label}
      onClick={onNavigate}
      className={({ isActive }) => `studio-nav-link${isActive ? ' is-current' : ''}`}
    >
      <span className="studio-nav-icon">
        <Icon aria-hidden="true" />
      </span>
      <span className="studio-nav-label">{item.label}</span>
    </NavLink>
  );
  return compact ? (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={12}
          collisionPadding={12}
          className="studio-nav-tooltip"
        >
          {item.label}
          <Tooltip.Arrow />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  ) : (
    link
  );
}

export function NavigationLinks({
  items,
  query,
  compact = false,
  mobile = false,
  onNavigate,
  onClear,
}: {
  items: readonly NavigationItem[];
  query: string;
  compact?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
  onClear: () => void;
}) {
  const { pathname } = useLocation();
  const current = currentNavigation(pathname, items);
  const groupId = current?.group;
  const id = useId();
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set([
        ...(mobile ? [] : ['main', 'sell', 'stock', 'business']),
        ...(groupId ? [groupId] : []),
      ]),
  );
  useEffect(() => {
    if (groupId) setExpanded((previous) => new Set([...previous, groupId]));
  }, [pathname, groupId]);
  const groups = navigationGroups(items, query);
  const searching = !!query.trim();
  return (
    <Tooltip.Provider delayDuration={180} skipDelayDuration={200}>
      <nav
        className="studio-nav-scroll"
        aria-label={mobile ? 'Mobile navigation' : 'Main navigation'}
      >
        {!groups.length ? (
          <div className="studio-nav-empty">
            <p>No matching pages.</p>
            <button type="button" onClick={onClear}>
              Clear search
            </button>
          </div>
        ) : (
          groups.map((group) => {
            const open = compact || searching || expanded.has(group.id);
            const linksId = `${id}-${group.id}`;
            return (
              <section className="studio-nav-group" key={group.id} aria-label={group.label}>
                {!compact && (
                  <h2>
                    <button
                      type="button"
                      className="studio-group-toggle"
                      disabled={searching}
                      aria-expanded={open}
                      aria-controls={linksId}
                      onClick={() =>
                        setExpanded((previous) => {
                          const next = new Set(previous);
                          next.has(group.id) ? next.delete(group.id) : next.add(group.id);
                          return next;
                        })
                      }
                    >
                      {group.label}
                      <ChevronDown aria-hidden="true" className={open ? 'is-open' : ''} />
                    </button>
                  </h2>
                )}
                <ul id={linksId} hidden={!open}>
                  {group.items.map((item) => (
                    <li key={item.path}>
                      <Destination item={item} compact={compact} onNavigate={onNavigate} />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}
      </nav>
    </Tooltip.Provider>
  );
}

export function NavigationBrand() {
  return (
    <div className="studio-brand">
      <BrandLogo className="studio-brand-logo" width={36} height={36} />
      <div className="studio-brand-copy">
        <p>KLOTHIQ</p>
        <span>Admin workspace</span>
      </div>
    </div>
  );
}

export function NavigationAccount({
  items,
  name,
  role,
  compact,
  onNavigate,
  onSignOut,
}: {
  items: readonly NavigationItem[];
  name: string;
  role: string | null | undefined;
  compact?: boolean;
  onNavigate?: () => void;
  onSignOut: () => void;
}) {
  const account = items.find((item) => item.path === '/my-account');
  const accountLink = account ? (
    <NavLink
      to={account.path}
      onClick={onNavigate}
      className="studio-account-link"
      aria-label="My Account"
    >
      <span className="studio-avatar" aria-hidden="true">
        {name.charAt(0).toUpperCase() || 'U'}
      </span>
      {!compact && (
        <span className="studio-account-copy">
          <strong title={name}>{name}</strong>
          <span>{roleLabel(role)}</span>
        </span>
      )}
    </NavLink>
  ) : null;
  return (
    <div className="studio-account">
      {compact && accountLink ? (
        <Tooltip.Provider delayDuration={180}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{accountLink}</Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={12}
                collisionPadding={12}
                className="studio-nav-tooltip"
              >
                My Account
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        accountLink
      )}
      <button
        type="button"
        className="studio-icon-button"
        onClick={onSignOut}
        aria-label="Sign out"
      >
        <LogOut aria-hidden="true" />
      </button>
    </div>
  );
}

export function StudioMobileNavigation({
  items,
  query,
  onQueryChange,
  onClose,
  name,
  role,
  onSignOut,
  scope,
}: {
  items: readonly NavigationItem[];
  query: string;
  onQueryChange: (value: string) => void;
  onClose: () => void;
  name: string;
  role: string | null | undefined;
  onSignOut: () => void;
  scope: string;
}) {
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="studio-navigation-overlay" data-field-guide-scope />
      <Dialog.Content
        className="studio-navigation-dialog"
        aria-describedby={undefined}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
          }
        }}
      >
        <div className="studio-mobile-heading">
          <NavigationBrand />
          <Dialog.Title className="sr-only">Navigation</Dialog.Title>
          <Dialog.Close asChild>
            <button type="button" className="studio-icon-button" aria-label="Close menu">
              <X aria-hidden="true" />
            </button>
          </Dialog.Close>
        </div>
        <NavigationSearch
          value={query}
          onChange={onQueryChange}
          label="Find a page in mobile navigation"
        />
        <NavigationLinks
          key={scope}
          items={items}
          query={query}
          mobile
          onNavigate={onClose}
          onClear={() => onQueryChange('')}
        />
        <NavigationAccount
          items={items}
          name={name}
          role={role}
          onNavigate={onClose}
          onSignOut={() => {
            onClose();
            onSignOut();
          }}
        />
      </Dialog.Content>
    </Dialog.Portal>
  );
}
